
import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../../../components/Form/Button";
import { ModalType } from "../../../../../../../../components/Generic/Modal";
import { CommodityCode } from "../../../../../../../../models/commodity.code";
import { ConfirmationModal, ErrorMessage, ItalicErrorMessage } from "./styles";
import { formatHSCode } from "../../utils";

export interface DeleteConfirmationModalHandles {
  open: (commodityCode: CommodityCode) => void;
  close: () => void;
}

interface ModalProps {
  closeModal?: () => void;
  onSubmit: (id: number, companyId: number) => void;
  loadingAction?: boolean;
}

const DeleteConfirmationModal: ForwardRefRenderFunction<
  DeleteConfirmationModalHandles,
  ModalProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { closeModal, onSubmit, loadingAction } = props;

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [commodityCode, setCommodityCode] = useState<CommodityCode>({} as CommodityCode);

  const open = useCallback((CommodityCode: CommodityCode) => {
    setCommodityCode(CommodityCode);
    setIsOpened(true);
  }, []);

  const close = useCallback(() => {
    setIsOpened(false);
    setCommodityCode({} as CommodityCode);
    if (closeModal) closeModal();
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleDelete = () => {
    if (!commodityCode?.id || !commodityCode.companyId) return;

    onSubmit(commodityCode.id, commodityCode.companyId);
  };

  return (
    <ConfirmationModal
      data-cy="delete-commodityCode-confirmation-modal"
      open={isOpened}
      type={ModalType.DELETE}
      handleClose={close}
      title={translate("deleteCommodityCode")}
      showActionButtons
      actionButtonProps={{
        onCancel: close,
        textSave: translate("delete"),
        loading: loadingAction,
        onSave: handleDelete,
        cancelDisabled: loadingAction,
        customColorCancel: Color.TEXT_INFO,
        customColorSave: Color.ERROR,
      }}
    >
      <Grid
        container
        spacing={2}
        className="mt-2"
        alignItems="flex-start"
        justifyContent="flex-start"
        direction="column"
      >
        <Grid item>
          <ErrorMessage>
            {`${translate("doYouReallyWantToDeleteThisCommodityCode")}${commodityCode?.hscode ? ` ${formatHSCode(commodityCode.hscode)}?` : "?"
              }`}
          </ErrorMessage>
        </Grid>
        <Grid item>
          <ItalicErrorMessage className="error">
            {translate("thisActionIsPermanentAndCannotBeUndone")}
          </ItalicErrorMessage>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(DeleteConfirmationModal);
